/**
 * TradeScreen — Prediction Market
 * WiTon/BTC pair, Long/Short, x5 multiplier, real BTC chart
 * Live line chart during active positions, candle chart otherwise
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../services/api';

interface KlineCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface ActivePosition {
  tradeId: string;
  direction: string;
  amount: number;
  multiplier: number;
  duration: number;
  entryPrice: number;
  expiresAt: string;
}

interface PriceTick {
  time: number;
  price: number;
}

const MULTIPLIER = 5;

export function TradeScreen() {
  const { t } = useTranslation();
  const [balance, setBalance] = useState(0);
  const [btcPrice, setBtcPrice] = useState<number>(0);
  const [klines, setKlines] = useState<KlineCandle[]>([]);
  const [amount, setAmount] = useState('15000');
  const [duration, setDuration] = useState<30 | 60>(30);
  const [position, setPosition] = useState<ActivePosition | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settling, setSettling] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [priceTicks, setPriceTicks] = useState<PriceTick[]>([]);
  const [resultTicks, setResultTicks] = useState<PriceTick[]>([]);
  const [resultEntry, setResultEntry] = useState<{price: number; direction: string} | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const priceIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const klinesIntervalRef = useRef<ReturnType<typeof setInterval>>();

  const fetchPrice = useCallback(async () => {
    try {
      const res = await api.getBtcPrice();
      setBtcPrice(res.data.price);
    } catch {}
  }, []);

  const fetchKlines = useCallback(async () => {
    try {
      const res = await api.getBtcKlines('1m', 60);
      setKlines(res.data.klines);
    } catch {}
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const profileRes = await api.getProfile();
        setBalance(Number(profileRes.data.balance));
        setIsAdmin(!!(profileRes.data as any).isAdmin);
      } catch {}
      setLoaded(true);
      try {
        const res = await api.getActivePosition();
        if (res.data.position) setPosition(res.data.position);
      } catch {}
    };
    init();
    fetchPrice();
    fetchKlines();
  }, []);

  // Price polling — 1s during active position, 3s otherwise
  useEffect(() => {
    const fast = !!position;
    priceIntervalRef.current = setInterval(fetchPrice, fast ? 1000 : 3000);
    klinesIntervalRef.current = setInterval(fetchKlines, fast ? 5000 : 3000);
    return () => {
      clearInterval(priceIntervalRef.current);
      clearInterval(klinesIntervalRef.current);
    };
  }, [fetchPrice, fetchKlines, position]);

  // Clear ticks when position ends
  useEffect(() => {
    if (!position) setPriceTicks([]);
  }, [position]);

  // Collect live price ticks during active position
  useEffect(() => {
    if (position && btcPrice > 0) {
      setPriceTicks(prev => [...prev, { time: Date.now(), price: btcPrice }]);
    }
  }, [btcPrice, position]);

  // Countdown
  useEffect(() => {
    if (!position) { setCountdown(0); return; }
    let settled = false;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((new Date(position.expiresAt).getTime() - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0 && !settled && !settling) {
        settled = true;
        doSettle(position.tradeId);
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => { clearInterval(iv); settled = true; };
  }, [position, settling]);

  // Draw chart — live line during position/result, candles otherwise
  useEffect(() => {
    if (!canvasRef.current) return;
    if (position && priceTicks.length > 0) {
      drawLiveChart(canvasRef.current, priceTicks, position.entryPrice, position.direction);
    } else if (result && resultTicks.length > 0 && resultEntry) {
      drawLiveChart(canvasRef.current, resultTicks, resultEntry.price, resultEntry.direction);
    } else if (klines.length > 0) {
      drawCandleChart(canvasRef.current, klines);
    }
  }, [klines, position, btcPrice, priceTicks, result, resultTicks, resultEntry]);

  const handleOpen = async (direction: 'long' | 'short') => {
    setError('');
    setResult(null);
    setResultTicks([]);
    setResultEntry(null);
    const numAmount = parseInt(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Enter a valid amount');
      return;
    }
    if (numAmount > balance) {
      setError(t('transfer.insufficient_balance') || 'Insufficient balance');
      return;
    }
    setLoading(true);
    try {
      const res = await api.openPosition({ direction, amount: numAmount, duration });
      setPosition({
        tradeId: res.data.tradeId,
        direction: res.data.direction,
        amount: res.data.amount,
        multiplier: res.data.multiplier,
        duration: res.data.duration,
        entryPrice: res.data.entryPrice,
        expiresAt: res.data.expiresAt,
      });
      setBalance(res.data.newBalance);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to open position');
    } finally {
      setLoading(false);
    }
  };

  const doSettle = async (tradeId: string) => {
    if (settling) return;
    setSettling(true);
    setLoading(true);
    try {
      const res = await api.settlePosition(tradeId);
      // Preserve chart data for result screen
      setResultTicks([...priceTicks]);
      if (position) {
        setResultEntry({ price: position.entryPrice, direction: position.direction });
      }
      setResult(res.data);
      setBalance(res.data.newBalance);
      setPosition(null);
    } catch (err: any) {
      if (err.response?.data?.error?.code === 'NOT_EXPIRED') {
        // not ready yet
      } else {
        setError(err.response?.data?.error?.message || 'Failed to settle');
        setPosition(null);
      }
    } finally {
      setLoading(false);
      setSettling(false);
    }
  };

  const potentialWin = parseInt(amount) * MULTIPLIER || 0;

  // Live P&L during active position
  const livePriceChange = position && btcPrice && position.entryPrice
    ? btcPrice - position.entryPrice : 0;
  const livePriceChangePct = position && position.entryPrice
    ? (livePriceChange / position.entryPrice) * 100 : 0;
  const priceIsFlat = position ? btcPrice === position.entryPrice : true;
  const isCurrentlyWinning = position && !priceIsFlat
    ? (position.direction === 'long' ? btcPrice > position.entryPrice : btcPrice < position.entryPrice)
    : false;

  // Result price % change
  const resultPriceChangePct = result
    ? ((result.exitPrice - result.entryPrice) / result.entryPrice * 100) : 0;

  // Color for potential P&L
  const potentialColor = priceIsFlat ? 'rgba(255,255,255,0.5)' : (isCurrentlyWinning ? '#10b981' : '#ef4444');

  if (loaded && !isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px 24px',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.1)',
          maxWidth: '320px',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
            {t('coming_soon', 'Coming Soon')}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.5 }}>
            {t('trade_coming_soon', 'Trading will be available soon. Stay tuned!')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      paddingBottom: '90px',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px',
        background: 'rgba(10, 22, 40, 0.8)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>WiTon / BTC</div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Prediction Market x{MULTIPLIER}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#10b981' }}>
            ${btcPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>BTC/USDT</div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ padding: '8px 12px' }}>
        <canvas
          ref={canvasRef}
          width={700}
          height={300}
          style={{
            width: '100%',
            height: '180px',
            borderRadius: '12px',
            background: 'rgba(10, 22, 40, 0.6)',
            border: '1px solid rgba(59, 130, 246, 0.15)',
          }}
        />
      </div>

      {/* Active Position */}
      {position && (
        <div style={{
          margin: '0 12px 12px',
          padding: '14px',
          borderRadius: '12px',
          background: position.direction === 'long'
            ? 'rgba(16, 185, 129, 0.15)'
            : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${position.direction === 'long' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{
              fontSize: '16px', fontWeight: 700,
              color: position.direction === 'long' ? '#10b981' : '#ef4444',
            }}>
              {position.direction === 'long' ? '↑ LONG' : '↓ SHORT'}
            </span>
            <span style={{ fontSize: '24px', fontWeight: 700, color: '#fbbf24' }}>
              {countdown}s
            </span>
          </div>

          {/* Progress bar */}
          <div style={{
            height: '3px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
            marginBottom: '10px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              background: '#fbbf24',
              borderRadius: '2px',
              transition: 'width 1s linear',
              width: `${Math.max(0, (1 - countdown / position.duration) * 100)}%`,
            }} />
          </div>

          {/* Entry + Stake */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
            <span>Entry: ${position.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
            <span>Stake: {position.amount.toLocaleString()} WiTon</span>
          </div>

          {/* Live price + % change + potential P&L */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '6px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#60a5fa' }}>
                Now: ${btcPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div style={{
                fontSize: '12px', fontWeight: 600, marginTop: '2px',
                color: priceIsFlat ? 'rgba(255,255,255,0.5)' : (livePriceChangePct >= 0 ? '#10b981' : '#ef4444'),
              }}>
                {livePriceChange >= 0 ? '+' : ''}{livePriceChange.toFixed(2)} ({livePriceChangePct >= 0 ? '+' : ''}{livePriceChangePct.toFixed(4)}%)
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', marginBottom: '2px' }}>
                Potential
              </div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: potentialColor }}>
                {priceIsFlat
                  ? '±0'
                  : isCurrentlyWinning
                    ? `+${(position.amount * MULTIPLIER).toLocaleString()}`
                    : `-${position.amount.toLocaleString()}`}
                <span style={{ fontSize: '11px', fontWeight: 400 }}> WiTon</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{
          margin: '0 12px 12px',
          padding: '16px',
          borderRadius: '12px',
          background: result.result === 'win' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          border: `1px solid ${result.result === 'win' ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
          textAlign: 'center' as const,
        }}>
          <div style={{ fontSize: '32px', marginBottom: '4px' }}>
            {result.result === 'win' ? '🎉' : '😞'}
          </div>

          <div style={{
            fontSize: '16px', fontWeight: 700, marginBottom: '4px',
            color: resultPriceChangePct >= 0 ? '#10b981' : '#ef4444',
          }}>
            BTC {resultPriceChangePct >= 0 ? '+' : ''}{resultPriceChangePct.toFixed(4)}%
          </div>

          <div style={{
            fontSize: '22px', fontWeight: 700, marginBottom: '6px',
            color: result.result === 'win' ? '#10b981' : '#ef4444',
          }}>
            {result.profitLoss > 0 ? '+' : ''}{result.profitLoss.toLocaleString()} WiTon
          </div>

          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
            ${result.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} → ${result.exitPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
            <span>{result.direction.toUpperCase()} x{MULTIPLIER}</span>
            <span>{result.duration || duration}s</span>
            <span>Stake: {result.amount.toLocaleString()}</span>
          </div>

          {result.statistics && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginBottom: '10px' }}>
              <span>Win: {result.statistics.winRate.toFixed(0)}%</span>
              {result.statistics.currentStreak > 0 && (
                <span>Streak: {result.statistics.currentStreak}</span>
              )}
              <span>Trades: {result.statistics.totalTrades}</span>
            </div>
          )}

          {result.tradingCoinEarned > 0 && (
            <div style={{ fontSize: '11px', color: '#fbbf24', marginBottom: '10px' }}>
              +{result.tradingCoinEarned} TC earned
            </div>
          )}

          <button
            onClick={() => { setResult(null); setResultTicks([]); setResultEntry(null); }}
            style={{
              marginTop: '4px', padding: '10px 32px',
              background: 'rgba(59, 130, 246, 0.3)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {result.result === 'win' ? 'Nice!' : 'Try Again'}
          </button>
        </div>
      )}

      {/* Trade Controls */}
      {!position && !result && (
        <div style={{ padding: '0 12px' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {([30, 60] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDuration(d)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  background: duration === d ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255,255,255,0.05)',
                  border: duration === d ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
                  color: duration === d ? '#60a5fa' : '#9ca3af',
                }}
              >
                {d}s
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Stake (WiTon)</span>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                Balance: {balance.toLocaleString()}
              </span>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="15000"
              style={{
                width: '100%', padding: '14px', boxSizing: 'border-box',
                background: 'rgba(10, 22, 40, 0.6)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '10px', color: '#fff', fontSize: '18px', fontWeight: 600, outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
              {[15000, 25000, 50000, 100000].map((v) => (
                <button
                  key={v}
                  onClick={() => setAmount(String(v))}
                  style={{
                    flex: 1, padding: '6px', borderRadius: '8px', fontSize: '11px', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#9ca3af',
                  }}
                >
                  {v >= 1000 ? `${v / 1000}K` : v}
                </button>
              ))}
            </div>
          </div>

          <div style={{
            textAlign: 'center', marginBottom: '12px',
            fontSize: '13px', color: 'rgba(255,255,255,0.6)',
          }}>
            Potential win: <span style={{ color: '#10b981', fontWeight: 700 }}>+{potentialWin.toLocaleString()} WiTon</span>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => handleOpen('long')}
              disabled={loading}
              style={{
                flex: 1, padding: '16px', borderRadius: '14px', fontSize: '18px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? 'rgba(16, 185, 129, 0.2)' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                border: 'none', color: '#fff',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(16, 185, 129, 0.3)',
              }}
            >
              LONG ↑
            </button>
            <button
              onClick={() => handleOpen('short')}
              disabled={loading}
              style={{
                flex: 1, padding: '16px', borderRadius: '14px', fontSize: '18px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? 'rgba(239, 68, 68, 0.2)' : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                border: 'none', color: '#fff',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(239, 68, 68, 0.3)',
              }}
            >
              SHORT ↓
            </button>
          </div>

          {error && (
            <div style={{
              marginTop: '12px', padding: '10px', borderRadius: '8px',
              background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', fontSize: '13px', textAlign: 'center',
            }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Draw live line chart during active position — focused on entry price area */
function drawLiveChart(canvas: HTMLCanvasElement, ticks: PriceTick[], entryPrice: number, direction: string) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;
  const pad = { top: 15, bottom: 20, left: 10, right: 65 };

  ctx.clearRect(0, 0, W, H);

  // Calculate Y range — centered on entry, with enough margin to see movement
  const tickPrices = ticks.map(t => t.price);
  const minTick = Math.min(...tickPrices);
  const maxTick = Math.max(...tickPrices);
  const spread = Math.max(maxTick - entryPrice, entryPrice - minTick, 0);
  const minMargin = entryPrice * 0.0003; // at least 0.03% each side (~$20 for BTC)
  const halfRange = Math.max(spread, minMargin) * 1.5;

  const minP = entryPrice - halfRange;
  const maxP = entryPrice + halfRange;

  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top - pad.bottom;
  const toY = (p: number) => pad.top + chartH - ((p - minP) / (maxP - minP)) * chartH;

  // Time range
  const startTime = ticks[0].time;
  const endTime = Math.max(ticks[ticks.length - 1].time, startTime + 5000);
  const toX = (t: number) => pad.left + ((t - startTime) / (endTime - startTime)) * chartW;

  // Grid lines
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.08)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 5; i++) {
    const y = pad.top + (chartH / 4) * i;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(W - pad.right, y);
    ctx.stroke();
  }

  // Price labels on right (5 levels)
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.font = '16px monospace';
  ctx.textAlign = 'left';
  for (let i = 0; i < 5; i++) {
    const y = pad.top + (chartH / 4) * i;
    const price = maxP - ((maxP - minP) / 4) * i;
    ctx.fillText(price.toFixed(1), W - pad.right + 4, y + 4);
  }

  // Entry price horizontal line
  const entryY = toY(entryPrice);
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = '#fbbf24';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(pad.left, entryY);
  ctx.lineTo(W - pad.right, entryY);
  ctx.stroke();
  ctx.setLineDash([]);

  // ENTRY label
  ctx.fillStyle = '#fbbf24';
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'left';
  ctx.fillText('ENTRY', pad.left + 4, entryY - 6);

  if (ticks.length < 2) {
    // Single dot
    ctx.fillStyle = '#60a5fa';
    ctx.beginPath();
    ctx.arc(toX(ticks[0].time), toY(ticks[0].price), 4, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  // Determine if currently winning
  const lastPrice = ticks[ticks.length - 1].price;
  const isLong = direction === 'long';
  const winning = isLong ? lastPrice > entryPrice : lastPrice < entryPrice;
  const isFlat = lastPrice === entryPrice;
  const lineColor = isFlat ? '#60a5fa' : (winning ? '#10b981' : '#ef4444');

  // Filled area between price line and entry
  ctx.beginPath();
  ctx.moveTo(toX(ticks[0].time), entryY);
  for (const tick of ticks) {
    ctx.lineTo(toX(tick.time), toY(tick.price));
  }
  ctx.lineTo(toX(ticks[ticks.length - 1].time), entryY);
  ctx.closePath();
  if (!isFlat) {
    ctx.fillStyle = winning ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)';
    ctx.fill();
  }

  // Price line
  ctx.beginPath();
  ctx.moveTo(toX(ticks[0].time), toY(ticks[0].price));
  for (let i = 1; i < ticks.length; i++) {
    ctx.lineTo(toX(ticks[i].time), toY(ticks[i].price));
  }
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Current price dot (last tick) with glow
  const lastTick = ticks[ticks.length - 1];
  const lastY = toY(lastTick.price);
  const lastX = toX(lastTick.time);

  ctx.fillStyle = lineColor.replace(')', ', 0.3)').replace('rgb', 'rgba');
  ctx.beginPath();
  ctx.arc(lastX, lastY, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = lineColor;
  ctx.beginPath();
  ctx.arc(lastX, lastY, 4, 0, Math.PI * 2);
  ctx.fill();

  // Current price dashed line across
  ctx.setLineDash([3, 3]);
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(pad.left, lastY);
  ctx.lineTo(W - pad.right, lastY);
  ctx.stroke();
  ctx.setLineDash([]);

  // Current price label
  ctx.fillStyle = lineColor;
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'left';
  const labelY = Math.abs(lastY - entryY) < 16 ? lastY + 16 : lastY + 4;
  ctx.fillText(lastPrice.toFixed(1), W - pad.right + 4, labelY);
}

/** Draw candlestick chart (when no active position) */
function drawCandleChart(canvas: HTMLCanvasElement, data: KlineCandle[]) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;
  const pad = { top: 10, bottom: 20, left: 10, right: 60 };

  ctx.clearRect(0, 0, W, H);

  if (data.length < 2) return;

  const prices = data.flatMap((c) => [c.high, c.low]);
  let minP = Math.min(...prices);
  let maxP = Math.max(...prices);
  const range = maxP - minP || 1;
  minP -= range * 0.05;
  maxP += range * 0.05;

  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top - pad.bottom;
  const candleW = Math.max(2, chartW / data.length - 1);

  const toY = (p: number) => pad.top + chartH - ((p - minP) / (maxP - minP)) * chartH;

  // Grid
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.08)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 5; i++) {
    const y = pad.top + (chartH / 4) * i;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(W - pad.right, y);
    ctx.stroke();

    const price = maxP - ((maxP - minP) / 4) * i;
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '16px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(price.toFixed(0), W - pad.right + 4, y + 4);
  }

  // Candles
  data.forEach((candle, i) => {
    const x = pad.left + (i / data.length) * chartW + candleW / 2;
    const isGreen = candle.close >= candle.open;

    ctx.strokeStyle = isGreen ? '#10b981' : '#ef4444';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, toY(candle.high));
    ctx.lineTo(x, toY(candle.low));
    ctx.stroke();

    const bodyTop = toY(Math.max(candle.open, candle.close));
    const bodyBot = toY(Math.min(candle.open, candle.close));
    const bodyH = Math.max(1, bodyBot - bodyTop);
    ctx.fillStyle = isGreen ? '#10b981' : '#ef4444';
    ctx.fillRect(x - candleW / 2, bodyTop, candleW, bodyH);
  });
}
